import { z } from "zod";
import { formatISODate } from "@/lib/site";

export const INQUIRY_TYPES = [
  "General Question",
  "RV Site",
  "Cabin or Room",
  "Tent Camping",
  "Extended or Seasonal Stay",
  "Corporate or Worker Housing",
  "Family Reunion or Group Stay",
  "Birthday or Special Event",
  "Existing Reservation",
  "Other",
] as const;

export type InquiryType = (typeof INQUIRY_TYPES)[number];

const phoneRegex = /^[+]?[\d\s().-]{7,20}$/;

export const contactFormSchema = z
  .object({
    firstName: z.string().trim().min(1, "First name is required.").max(80),
    lastName: z.string().trim().min(1, "Last name is required.").max(80),
    email: z.string().trim().email("Enter a valid email address.").max(254),
    phone: z.string().trim().max(30),
    preferredResponse: z.enum(["Email", "Phone"], {
      required_error: "Choose a preferred response method.",
    }),
    inquiryType: z.enum(INQUIRY_TYPES, {
      required_error: "Select an inquiry type.",
      invalid_type_error: "Select an inquiry type.",
    }),
    arrivalDate: z.string(),
    departureDate: z.string(),
    subject: z.string().trim().max(160),
    message: z
      .string()
      .trim()
      .min(10, "Please share a bit more detail in your message.")
      .max(2000, "Messages are limited to 2,000 characters."),
    /** Honeypot — server treats non-empty as spam (silent success). */
    website: z.string().max(200),
    /** Client timestamp when the form was first opened. */
    formStartedAt: z.number().int().positive(),
    referringPage: z.string().max(500),
    timezone: z.string().max(100),
  })
  .superRefine((data, ctx) => {
    const today = formatISODate(new Date());

    if (data.preferredResponse === "Phone" && !data.phone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phone"],
        message: "Phone number is required when you prefer a phone call.",
      });
    }

    if (data.phone && !phoneRegex.test(data.phone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phone"],
        message: "Enter a valid telephone number.",
      });
    }

    if (data.arrivalDate && data.arrivalDate < today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["arrivalDate"],
        message: "Arrival date cannot be in the past.",
      });
    }

    if (data.arrivalDate && data.departureDate && data.departureDate <= data.arrivalDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["departureDate"],
        message: "Departure must be later than arrival.",
      });
    }
  });

export type ContactFormValues = z.infer<typeof contactFormSchema>;

export type ContactApiSuccess = {
  ok: true;
  message: string;
};

export type ContactApiError = {
  ok: false;
  message: string;
  fieldErrors?: Record<string, string[]>;
};

export type ContactApiResponse = ContactApiSuccess | ContactApiError;
